import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { Controller, useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Grid } from '@material-ui/core'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { postNamedCommand } from '../../../../../service'
import SydModal from '../../../../commonDesign/SydModal'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../commonDesign/Button'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydDatePicker from '../../../../commonDesign/SydDatePicker'
import SydTextArea from '../../../../commonDesign/SydTextArea'
import SydSwitch from '../../../../commonDesign/SydSwitch'
import Markup from '../../../../atoms/Markup'
import { fetchEventCategories } from './wealthboxQueryHelpers'
import { htmlToPlainText } from './wealthboxHtml'
import { extractWealthboxErrorMessage } from './wealthboxErrors'

// Wealthbox event `state` enum (documented choices).
const STATE_OPTIONS = [
  { label: '(None)', value: '' },
  { label: 'Unconfirmed', value: 'unconfirmed' },
  { label: 'Confirmed', value: 'confirmed' },
  { label: 'Tentative', value: 'tentative' },
  { label: 'Completed', value: 'completed' },
  { label: 'Cancelled', value: 'cancelled' }
]

const MODE_VIEWING = 'viewing'
const MODE_EDITING = 'editing'
const MODE_CONFIRMING_DELETE = 'confirming-delete'

const labelStyle = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-color-subtle)',
  marginBottom: '4px'
}
const valueStyle = { fontSize: 'var(--font-size-sm)', marginBottom: '12px' }

// Datetime form values use a local-aware ISO string. SydDatePicker emits date
// values; we combine with time inputs for the calendar form mode. For simple
// mode the form only supports the start date.
function toDateInput (iso) {
  return iso ? dayjs(iso).format('YYYY-MM-DD') : ''
}
function toTimeInput (iso) {
  return iso ? dayjs(iso).format('HH:mm') : ''
}
function combineDateTime (date, time, allDay) {
  if (!date) return ''
  if (allDay) return dayjs(date).startOf('day').toISOString()
  return dayjs(`${date}T${time || '00:00'}`).toISOString()
}

function buildFormDefaults (event) {
  return {
    title: event?.title || '',
    starts_date: toDateInput(event?.starts_at),
    starts_time: toTimeInput(event?.starts_at),
    ends_date: toDateInput(event?.ends_at),
    ends_time: toTimeInput(event?.ends_at),
    all_day: !!event?.all_day,
    location: event?.location || '',
    // Wealthbox stores event descriptions as HTML (same as notes/tasks).
    // Strip to plaintext for the textarea editor.
    description: htmlToPlainText(event?.description || ''),
    event_category: event?.event_category ?? '',
    state: event?.state || ''
  }
}

function ViewingBody ({ event, categoryOptions }) {
  // Render all linked contacts (events frequently link to multiple household
  // members - e.g. a couples-meeting linked to both spouses).
  const linkedNames = Array.isArray(event.linked_to)
    ? event.linked_to.map(l => l?.name).filter(Boolean)
    : []
  const linkedLabel = linkedNames.length ? linkedNames.join(', ') : null
  const start = event.starts_at ? dayjs(event.starts_at) : null
  const end = event.ends_at ? dayjs(event.ends_at) : null
  const categoryLabel = useMemo(() => {
    if (event.event_category == null) return null
    return categoryOptions.find(c => c.value === event.event_category)?.label ?? `Category #${event.event_category}`
  }, [event.event_category, categoryOptions])

  const whenLabel = !start?.isValid()
    ? null
    : event.all_day
      ? `${start.format('ddd, MMM D, YYYY')} (all day)`
      : `${start.format('ddd, MMM D, YYYY h:mm A')}${end?.isValid() ? ` – ${end.format('h:mm A')}` : ''}`

  return (
    <Grid container spacing={2}>
      {linkedLabel ? (
        <Grid item xs={12} sm={6}>
          <div style={labelStyle}>{linkedNames.length > 1 ? 'Linked Contacts' : 'Linked Contact'}</div>
          <div style={valueStyle}>{linkedLabel}</div>
        </Grid>
      ) : null}
      {whenLabel ? (
        <Grid item xs={12} sm={linkedLabel ? 6 : 12}>
          <div style={labelStyle}>When</div>
          <div style={valueStyle}>{whenLabel}</div>
        </Grid>
      ) : null}
      {event.location ? (
        <Grid item xs={12} sm={6}>
          <div style={labelStyle}>Location</div>
          <div style={valueStyle}>{event.location}</div>
        </Grid>
      ) : null}
      {event.state ? (
        <Grid item xs={12} sm={event.location ? 6 : 12}>
          <div style={labelStyle}>State</div>
          <div style={valueStyle}>{STATE_OPTIONS.find(o => o.value === event.state)?.label || event.state}</div>
        </Grid>
      ) : null}
      {categoryLabel ? (
        <Grid item xs={12}>
          <div style={labelStyle}>Category</div>
          <div style={valueStyle}>{categoryLabel}</div>
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <div style={labelStyle}>Description</div>
        {event.description
          ? (
            // Wealthbox stores event descriptions as HTML (same as notes and
            // task descriptions). Sanitize + render via Markup.
            <Markup
              value={event.description}
              type='html'
              style={{ ...valueStyle, fontWeight: 'inherit' }}
            />
          )
          : (
            <div style={{ ...valueStyle, color: 'var(--text-color-subtle)', fontStyle: 'italic' }}>No description</div>
          )}
      </Grid>
    </Grid>
  )
}
ViewingBody.propTypes = { event: PropTypes.object.isRequired, categoryOptions: PropTypes.array }

function EditingBody ({ event, formMode, categoryOptions, onSubmit, onCancel }) {
  const isCalendar = formMode === 'calendar'
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const { control, handleSubmit, watch } = useForm({ defaultValues: buildFormDefaults(event) })
  const allDay = watch('all_day')

  const submit = handleSubmit(async (values) => {
    setSubmitting(true)
    setSubmitError(null)
    try {
      // Wealthbox PUT /v1/events/{id} - all fields optional, send only what
      // changed. Combine the form's separate date + time fields back into
      // ISO strings (or midnight-local for all_day events).
      const body = {
        title: values.title?.trim(),
        starts_at: combineDateTime(values.starts_date, values.starts_time, values.all_day),
        ends_at: combineDateTime(values.ends_date, values.ends_time, values.all_day),
        all_day: !!values.all_day
      }
      if (values.location !== undefined) body.location = values.location || null
      if (values.description !== undefined) body.description = values.description || null
      if (values.event_category !== '' && values.event_category != null) {
        body.event_category = Number(values.event_category)
      } else {
        body.event_category = null
      }
      if (values.state) body.state = values.state
      await onSubmit(body)
    } catch (err) {
      setSubmitError(extractWealthboxErrorMessage(err, 'Failed to save event'))
    } finally {
      setSubmitting(false)
    }
  })

  return (
    <form onSubmit={submit}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            name='title'
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <SydLabel label='Title' required>
                <SydInput {...field} value={field.value || ''} placeholder='Event title' />
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name='starts_date'
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <SydLabel label='Start Date' required>
                <SydDatePicker
                  type='date'
                  dateFormat='YYYY-MM-DD'
                  value={field.value || null}
                  onChange={(d) => field.onChange(d ? dayjs(d).format('YYYY-MM-DD') : '')}
                />
              </SydLabel>
            )}
          />
        </Grid>
        {!allDay && (
          <Grid item xs={12} sm={6}>
            <Controller
              name='starts_time'
              control={control}
              render={({ field }) => (
                <SydLabel label='Start Time'>
                  <SydInput type='time' {...field} value={field.value || ''} />
                </SydLabel>
              )}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <Controller
            name='ends_date'
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <SydLabel label='End Date' required>
                <SydDatePicker
                  type='date'
                  dateFormat='YYYY-MM-DD'
                  value={field.value || null}
                  onChange={(d) => field.onChange(d ? dayjs(d).format('YYYY-MM-DD') : '')}
                />
              </SydLabel>
            )}
          />
        </Grid>
        {!allDay && (
          <Grid item xs={12} sm={6}>
            <Controller
              name='ends_time'
              control={control}
              render={({ field }) => (
                <SydLabel label='End Time'>
                  <SydInput type='time' {...field} value={field.value || ''} />
                </SydLabel>
              )}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Controller
            name='all_day'
            control={control}
            render={({ field }) => (
              <label className='flex items-center gap-2' style={{ minHeight: '54px', cursor: 'pointer' }}>
                <SydSwitch
                  label='All day'
                  value={!!field.value}
                  onChange={(_event, newValue) => field.onChange(newValue)}
                />
                <span className='font-size-sm'>All-day event</span>
              </label>
            )}
          />
        </Grid>

        {isCalendar && (
          <>
            <Grid item xs={12} sm={6}>
              <Controller
                name='location'
                control={control}
                render={({ field }) => (
                  <SydLabel label='Location'>
                    <SydInput {...field} value={field.value || ''} placeholder='Office, Zoom URL, etc.' />
                  </SydLabel>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name='state'
                control={control}
                render={({ field }) => (
                  <SydLabel label='State'>
                    <SydSelect
                      options={STATE_OPTIONS}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  </SydLabel>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='event_category'
                control={control}
                render={({ field }) => (
                  <SydLabel label='Category'>
                    <SydSelect
                      options={[{ label: '(None)', value: '' }, ...categoryOptions]}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const raw = e.target.value
                        field.onChange(raw === '' ? '' : Number(raw))
                      }}
                    />
                  </SydLabel>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='description'
                control={control}
                render={({ field }) => (
                  <SydLabel label='Description'>
                    <SydTextArea {...field} value={field.value || ''} rows={4} placeholder='Description' />
                  </SydLabel>
                )}
              />
            </Grid>
          </>
        )}
      </Grid>

      {submitError ? (
        <div className='font-size-xs mt-2' style={{ color: 'var(--color-decline-500)' }}>{submitError}</div>
      ) : null}

      <SydModalActions>
        <SydButton variant='ghost' size='sm' onClick={onCancel} disabled={submitting} type='button'>Cancel</SydButton>
        <SydButton variant='primary' size='sm' processing={submitting} type='submit'>Save Changes</SydButton>
      </SydModalActions>
    </form>
  )
}
EditingBody.propTypes = {
  event: PropTypes.object.isRequired,
  formMode: PropTypes.oneOf(['simple', 'calendar']),
  categoryOptions: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

function WealthboxEventDetailModal ({ open, onClose, event, enableEdit = false, formMode = 'calendar', invalidateQueryKeys }) {
  const { clientId } = useAppContext()
  const queryClient = useQueryClient()
  const [mode, setMode] = useState(MODE_VIEWING)
  const [categoryOptions, setCategoryOptions] = useState([])

  useEffect(() => { if (open) setMode(MODE_VIEWING) }, [open, event?.id])

  useEffect(() => {
    if (!open || !enableEdit || formMode !== 'calendar') return
    let cancelled = false
    fetchEventCategories({ clientId })
      .then(opts => { if (!cancelled) setCategoryOptions(opts) })
      .catch(() => { /* non-critical */ })
    return () => { cancelled = true }
  }, [open, enableEdit, formMode, clientId])

  const invalidateAll = useCallback(() => {
    if (!invalidateQueryKeys) return
    for (const key of invalidateQueryKeys) queryClient.invalidateQueries({ queryKey: key })
  }, [invalidateQueryKeys, queryClient])

  const { mutateAsync: handleEditSubmit } = useMutation({
    mutationFn: (body) => postNamedCommand('coreData', 'execute-wealthbox-domain-command', {
      command: 'modifyEvent',
      clientId,
      request: { eventId: event.id, body }
    }),
    onSuccess: () => { onClose(); invalidateAll() }
  })

  const { mutate: handleDelete, isLoading: deleting, error: deleteError } = useMutation({
    mutationFn: () => postNamedCommand('coreData', 'execute-wealthbox-domain-command', {
      command: 'deleteEvent',
      clientId,
      request: { eventId: event.id }
    }),
    onSuccess: () => { onClose(); invalidateAll() }
  })

  if (!event) return null

  const titles = {
    [MODE_VIEWING]: event.title || 'Event',
    [MODE_EDITING]: 'Edit Event',
    [MODE_CONFIRMING_DELETE]: 'Delete Event?'
  }

  return (
    <SydModal open={open} onClose={onClose} title={titles[mode]} size='xmedium' disablePortal={false}>
      {mode === MODE_VIEWING && (
        <>
          <ViewingBody event={event} categoryOptions={categoryOptions} />
          <SydModalActions>
            {enableEdit ? (
              <SydButton variant='ghost' size='sm' icon='delete' onClick={() => setMode(MODE_CONFIRMING_DELETE)}>Delete</SydButton>
            ) : null}
            <div style={{ flex: 1 }} />
            <SydButton variant='ghost' size='sm' onClick={onClose}>Close</SydButton>
            {enableEdit ? (
              <SydButton variant='primary' size='sm' icon='edit' onClick={() => setMode(MODE_EDITING)}>Edit</SydButton>
            ) : null}
          </SydModalActions>
        </>
      )}

      {mode === MODE_EDITING && (
        <EditingBody
          event={event}
          formMode={formMode}
          categoryOptions={categoryOptions}
          onSubmit={handleEditSubmit}
          onCancel={() => setMode(MODE_VIEWING)}
        />
      )}

      {mode === MODE_CONFIRMING_DELETE && (
        <>
          <div className='font-size-sm' style={{ padding: '4px 0 8px' }}>
            Delete <strong>{event.title || 'this event'}</strong>? This cannot be undone — the event will be removed from Wealthbox entirely.
          </div>
          {deleteError ? (
            <div className='font-size-xs mt-2' style={{ color: 'var(--color-decline-500)' }}>{deleteError?.message || 'Failed to delete event'}</div>
          ) : null}
          <SydModalActions>
            <SydButton variant='ghost' size='sm' onClick={() => setMode(MODE_VIEWING)} disabled={deleting}>Cancel</SydButton>
            <SydButton variant='primary' size='sm' processing={deleting} onClick={() => handleDelete()}>Delete</SydButton>
          </SydModalActions>
        </>
      )}
    </SydModal>
  )
}

WealthboxEventDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  event: PropTypes.object,
  enableEdit: PropTypes.bool,
  formMode: PropTypes.oneOf(['simple', 'calendar']),
  invalidateQueryKeys: PropTypes.arrayOf(PropTypes.array)
}

export default WealthboxEventDetailModal
